import { useSession } from '@/auth/useSession';
import { CopyableLink } from '@/components/copyable-link';
import { EditableInfoPage, SubmitProps } from '@/components/editable-info-page';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextArea } from '@/components/forms/form-text-area';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { LoadingPage } from '@/components/loading-page';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  EmptyArray,
  getAssessments,
  getLesson,
  getLessonAssessmentContentOverrides,
  updateLesson,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { textAreaValueToArray } from '@/lib/string-utils';
import { LearningObjectivesCell } from '@/routes/_layout/lessons/-components/learning-objectives-cell';
import { isAirAdmin, isCoach, nullsToUndefined } from '@airreading/common';
import { optionalFormNumber, UpdateLessonOptions } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { DefaultValues } from 'react-hook-form';
import isURL from 'validator/es/lib/isURL';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/lessons/$lessonId')({
  beforeLoad: validateRouteRole(isCoach),
  component: Page,
});

const editLessonFormSchema = z.object({
  name: z.string().trim().min(1, { message: 'Lesson name is required' }),
  handouts: z
    .string()
    .optional()
    .refine((val) => {
      const handoutLinks = textAreaValueToArray(val);
      return handoutLinks.length === 0 || handoutLinks.every((link) => isURL(link));
    }, 'Handout links must all be valid URLs'),
  objectives: z.string(),
  lessonLinkKTo2: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  lessonLink3To5: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  lessonLink6To8: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  assessmentIds: z.array(z.string()),
  shouldSkipFeedback: z.boolean().optional(),
  minWPM: optionalFormNumber(z.coerce.number().nonnegative().int()),
  maxWPM: optionalFormNumber(z.coerce.number().nonnegative().int()),
  minAccuracyPercent: optionalFormNumber(z.coerce.number().nonnegative()),
  maxAccuracyPercent: optionalFormNumber(z.coerce.number().nonnegative()),
  floridaStandard: z.string().optional(),
  arkansasStandard: z.string().optional(),
});

type EditLessonFormschema = z.infer<typeof editLessonFormSchema>;

function Page() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <LessonDetail />
    </Suspense>
  );
}

function LessonDetail() {
  const { actingUser } = useSession();
  const { lessonId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery(getLesson(lessonId));

  const { data: allAssessments = [] } = useQuery(getAssessments());
  const { data: assessmentContentOverrides = [] } = useQuery(getLessonAssessmentContentOverrides(lessonId));

  const { mutateAsync } = useMutation({
    mutationFn: updateLesson,
  });

  const { id, ...rest } = data;

  const defaultValues: DefaultValues<EditLessonFormschema> = nullsToUndefined({
    ...rest,
    handouts: (data.handouts || EmptyArray).join('\n'),
    objectives: (data.objectives || EmptyArray).join('\n'),
    lessonLinkKTo2: data.lessonLinkKTo2,
    lessonLink3To5: data.lessonLink3To5,
    lessonLink6To8: data.lessonLink6To8,
    assessmentIds: (data.assessments || EmptyArray).map((assessment) => assessment.id),
    shouldSkipFeedback: data.shouldSkipFeedback,
    minWPM: data.minWPM,
    maxWPM: data.maxWPM,
    minAccuracyPercent: data.minAccuracyPercent,
    maxAccuracyPercent: data.maxAccuracyPercent,
    floridaStandard: data.floridaStandard || '',
    arkansasStandard: data.arkansasStandard || '',
  });

  const onSubmit = ({ formData, disableEditing }: SubmitProps<EditLessonFormschema>) => {
    const { ...rest } = formData;

    const postLessonBody: UpdateLessonOptions = {
      ...rest,
      name: formData.name,
      lessonLinkKTo2: formData.lessonLinkKTo2 || null,
      lessonLink3To5: formData.lessonLink3To5 || null,
      lessonLink6To8: formData.lessonLink6To8 || null,
      handouts: textAreaValueToArray(formData.handouts),
      objectives: textAreaValueToArray(formData.objectives),
      shouldSkipFeedback: formData.shouldSkipFeedback,
      minWPM: formData.minWPM ?? null,
      maxWPM: formData.maxWPM ?? null,
      minAccuracyPercent: formData.minAccuracyPercent,
      maxAccuracyPercent: formData.maxAccuracyPercent,
      floridaStandard: formData.floridaStandard || null,
      arkansasStandard: formData.arkansasStandard || null,
    };

    return mutateAsync({ id, data: postLessonBody })
      .then(() => {
        queryClient.invalidateQueries(getLesson(lessonId));
        // Lesson lists and typeaheads (e.g. starting-lesson pickers) cache names for 30s, so a rename needs them dropped.
        queryClient.invalidateQueries({ queryKey: ['LESSONS'] });
        queryClient.invalidateQueries({ queryKey: ['LESSONS_TYPEAHEAD'] });
        queryClient.invalidateQueries({ queryKey: ['LESSONS_TYPEAHEAD_OPTIONS'] });
        toast({
          title: 'Lesson updated successfully',
        });
        disableEditing();
      })
      .catch((error: unknown) => {
        toast({
          title: 'Lesson update failed',
          description: airServerErrorFrom(error)?.userFacingMessage,
        });
      });
  };

  return (
    <EditableInfoPage
      headerText={data.name}
      formSchema={editLessonFormSchema}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      readonly={!isAirAdmin(actingUser)}
      breadcrumbs={
        isAirAdmin(actingUser)
          ? { linkItems: [{ label: 'Lessons', link: { to: '/lessons' } }], currentPage: data.name }
          : undefined
      }
    >
      {({ form, editing }) => {
        return (
          <>
            <InfoCard headerText={`Lesson details`}>
              <InfoKeyValue
                label="Lesson name"
                value={data.name}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="name"
                      label="Lesson name"
                      inputProps={{ placeholder: 'Lesson name' }}
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Learning objectives"
                value={
                  <div>
                    <LearningObjectivesCell objectives={data.objectives || EmptyArray} />
                  </div>
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormTextArea
                      form={form}
                      name="objectives"
                      label="Learning objectives (one objective per line)"
                      textAreaProps={{ placeholder: 'Learning objectives, one objective per line' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="# Min WPM"
                value={data.minWPM != null ? data.minWPM.toString() : '-'}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="minWPM"
                      label="# Min WPM"
                      inputProps={{ type: 'number', min: 0, step: 1, placeholder: 'e.g. 80' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="# Max WPM"
                value={data.maxWPM != null ? data.maxWPM.toString() : '-'}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="maxWPM"
                      label="# Max WPM"
                      inputProps={{ type: 'number', min: 0, step: 1, placeholder: 'e.g. 120' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Min Accuracy"
                value={data.minAccuracyPercent != null ? `${data.minAccuracyPercent}%` : '-'}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="minAccuracyPercent"
                      label="Min Accuracy"
                      inputProps={{ type: 'number', min: 0, step: 0.01, placeholder: 'e.g. 35.13' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Max Accuracy"
                value={data.maxAccuracyPercent != null ? `${data.maxAccuracyPercent}%` : '-'}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="maxAccuracyPercent"
                      label="Max Accuracy"
                      inputProps={{ type: 'number', min: 0, step: 0.01, placeholder: 'e.g. 80.5' }}
                    />
                  ),
                }}
              />
              <div className="col-span-3 grid grid-cols-1 gap-6 md:col-span-4 md:grid-cols-4 md:gap-x-0">
                <div className="md:col-span-2">
                  <InfoKeyValue
                    label="Florida standards"
                    value={data.floridaStandard?.trim() ? data.floridaStandard : '-'}
                    edit={{
                      editing,
                      editModeComponent: <FormTextArea form={form} name="floridaStandard" label="Florida standards" />,
                    }}
                  />
                </div>
                <div className="md:col-span-2">
                  <InfoKeyValue
                    label="Arkansas standards"
                    value={data.arkansasStandard?.trim() ? data.arkansasStandard : '-'}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormTextArea form={form} name="arkansasStandard" label="Arkansas standards" />
                      ),
                    }}
                  />
                </div>
              </div>
              <div className="col-span-3">
                <InfoKeyValue
                  label="Assessments"
                  value={
                    <div>
                      {data.assessments?.map((assessment) => {
                        const selectedContentOverrides = assessmentContentOverrides.filter(
                          ({ assessmentId }) => assessmentId === assessment.id,
                        );
                        const selectedContents = assessment.contents.filter(({ id }) =>
                          selectedContentOverrides.some(({ assessmentContentId }) => assessmentContentId === id),
                        );
                        if (selectedContents.length === 0) {
                          return <div key={assessment.id}> {assessment.name}</div>;
                        }

                        return (
                          <div key={assessment.id}>
                            {assessment.name}
                            <div className="pl-4">
                              {selectedContents.map((content) => (
                                <div key={content.id}>{content.name}</div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormCheckboxGroup
                        label="Assessments"
                        form={form}
                        name="assessmentIds"
                        options={allAssessments.map(({ id }) => id)}
                        optionLabel={(id) => allAssessments.find(({ id: assessmentId }) => assessmentId === id)?.name}
                      />
                    ),
                  }}
                />
              </div>
            </InfoCard>
            <InfoCard headerText="Lesson materials">
              <InfoKeyValue
                label="Lesson link for K-2"
                value={
                  data.lessonLinkKTo2 ? (
                    <CopyableLink externalHref={data.lessonLinkKTo2}>{data.lessonLinkKTo2}</CopyableLink>
                  ) : (
                    'No lesson link for K-2 available'
                  )
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="lessonLinkKTo2"
                      label="Lesson link for K-2"
                      inputProps={{ placeholder: 'Lesson slides url for K-2' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Lesson link for 3-5"
                value={
                  data.lessonLink3To5 ? (
                    <CopyableLink externalHref={data.lessonLink3To5}>{data.lessonLink3To5}</CopyableLink>
                  ) : (
                    'No lesson link for 3-5 available'
                  )
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="lessonLink3To5"
                      label="Lesson link for 3-5"
                      inputProps={{ placeholder: 'Lesson slides url for 3-5' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Lesson link for 6-8"
                value={
                  data.lessonLink6To8 ? (
                    <CopyableLink externalHref={data.lessonLink6To8}>{data.lessonLink6To8}</CopyableLink>
                  ) : (
                    'No lesson link for 6-8 available'
                  )
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="lessonLink6To8"
                      label="Lesson link for 6-8"
                      inputProps={{ placeholder: 'Lesson slides url for 6-8' }}
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Handouts"
                value={
                  <div>
                    {data.handouts
                      ? data.handouts.map((handout) => (
                          <CopyableLink key={handout} externalHref={handout}>
                            {handout}
                          </CopyableLink>
                        ))
                      : 'No handouts available'}
                  </div>
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormTextArea
                      form={form}
                      name="handouts"
                      label="Handouts (one link per line)"
                      textAreaProps={{ placeholder: 'Handouts' }}
                    />
                  ),
                }}
              />
            </InfoCard>
          </>
        );
      }}
    </EditableInfoPage>
  );
}
